import cx from 'classnames';

import {
  TooltipContent,
  TooltipRoot,
  TooltipTrigger
} from 'components/shared/Tooltip';
import AddToList from './card-actions/AddToList';
import Like from './card-actions/Like';
import Share from './card-actions/Share';

import { Playlist, Video } from 'api';
import type { SailthruFormattedItem } from 'lib/sailthru';
import type { JSXElementConstructor, ReactElement } from 'react';
import useShortURLToolTip from './hooks/useShortURLToolTip';

type PlaylistItem = Playlist & {
  isPlaylist: true;
};

type TooltipContentItem =
  | Video
  | PlaylistItem
  | SailthruFormattedItem
  | { id?: string; slug?: string; title?: string; [key: string]: any };

type CardToolTipProps = {
  talk: TooltipContentItem;
  isOpen?: boolean;
  isDarkMode?: boolean;
  children: ReactElement<any, string | JSXElementConstructor<any>>;
  onHide: () => void;
  mixpanelTitle: string;
  onShareClick?: () => void;
};

const CardToolTip = ({
  isOpen = false,
  children,
  onHide,
  talk,
  isDarkMode = false,
  mixpanelTitle,
  onShareClick
}: CardToolTipProps): React.ReactNode => {
  // Determine if this is a playlist
  const isPlaylist = Boolean(
    talk && typeof talk === 'object' && 'isPlaylist' in talk && talk.isPlaylist
  );

  // Extract the ID (works for both videos and playlists)
  const itemId = talk?.id || '';

  // For share functionality, we need to normalize the data
  const shareData = {
    id: talk?.id || '',
    slug: talk?.slug || '',
    title: talk?.title || '',
    canonicalUrl: ('canonicalUrl' in talk ? talk.canonicalUrl : '') || '',
    // For playlists, we don't have presenter info
    presenterDisplayName: isPlaylist
      ? ''
      : (talk as Partial<Video>)?.presenterDisplayName || '',
    // Set appropriate context based on content type
    videoContext: isPlaylist
      ? 'Playlist'
      : (talk as Partial<Video>)?.videoContext || 'TED Talk'
  };

  // Provide a fallback URL if canonicalUrl is not available
  const shareUrl = shareData.canonicalUrl || '';
  const { shortURL } = useShortURLToolTip(shareUrl, isOpen);

  // Use shortURL if available, otherwise fall back to the original URL
  // This ensures sharing works even if URL shortening fails
  const finalShortURL = shortURL || shareUrl;

  const cardClasses = cx(
    'flex flex-col rounded-lg shadow-[0px_4px_15px_0px_rgba(0,0,0,0.1)]',
    {
      'bg-textSecondary-onLight': isDarkMode,
      'bg-textPrimary-onDark': !isDarkMode
    }
  );

  return (
    <TooltipRoot open={isOpen} onOpenChange={open => !open && onHide()}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        side="bottom"
        align="start"
        sideOffset={5}
        collisionPadding={15}
        className="border-0 bg-transparent p-0 shadow-none"
        onPointerDownOutside={e => {
          const target = e.target as Element;
          // Don't close the tooltip if the user is clicking on a modal or modal overlay
          const isModalClick =
            target.closest('[role="dialog"]') ||
            target.closest('.modal-overlay') ||
            target.closest('[data-modal]');

          if (isModalClick) {
            e.preventDefault();
            return;
          }

          // Allow natural tooltip closing for other clicks
        }}
      >
        <div className={cardClasses}>
          <AddToList
            id={itemId}
            isOpen={isOpen}
            isDarkMode={isDarkMode}
            mixpanelTitle={mixpanelTitle}
          />
          <Like
            id={itemId}
            isOpen={isOpen}
            isDarkMode={isDarkMode}
            mixpanelTitle={mixpanelTitle}
          />
          <Share
            slug={shareData.slug}
            title={shareData.title}
            presenters={shareData.presenterDisplayName}
            context={shareData.videoContext}
            shortenedUrl={finalShortURL}
            onHideTooltip={onHide}
            isOpen={isOpen}
            isDarkMode={isDarkMode}
            mixpanelTitle={mixpanelTitle}
            onShareClick={onShareClick}
          />
        </div>
      </TooltipContent>
    </TooltipRoot>
  );
};

export default CardToolTip;
